/**
 * computed values and derived state for metadoc page
 * centralizes all calculation logic for stats, button states, and job tracking
 */
import { useMemo } from "react";

import type { DocumentGroup } from "services/document-group-api";
import type { GroupingSource, Metadoc } from "services/metadoc-api";
import type { BackgroundJobDataType } from "types/DataTypes";

type UseMetadocComputedValuesArgs = {
  metadoc?: Metadoc;
  metadocId?: string;
  opsGroups: DocumentGroup[];
  activeGroups: DocumentGroup[];
  groupingSource: GroupingSource;
  backgroundJobsData?: { results: BackgroundJobDataType[] };
  isAIProcessing: boolean;
  isRunningAIModel: boolean;
};

type PageStats = {
  coveredPages: number;
  totalPages: number;
  percentage: number;
};

type UseMetadocComputedValuesReturn = {
  // page classification stats
  opsPageStats: PageStats;
  activePageStats: PageStats;

  // AI button gating
  isRunAiModelReady: boolean;
  isRunAiModelDisabled: boolean;
  runAiModelDisabledReason?: string;

  // AI job tracking
  hasActiveAIJob: boolean;
  currentAIJob: BackgroundJobDataType | null;
  lastCompletedAIJob: BackgroundJobDataType | null;
  processingJobStatus?: string;
};

const calculatePageStats = (
  pageCount: number | null,
  groups: DocumentGroup[],
): PageStats => {
  if (!pageCount) {
    return { coveredPages: 0, totalPages: 0, percentage: 0 };
  }

  const totalPages = pageCount;
  const coveredPages = new Set<number>();

  // mark all pages covered by document groups
  for (const group of groups) {
    for (let page = group.start_pg; page <= group.end_pg; page++) {
      coveredPages.add(page);
    }
  }

  return {
    coveredPages: coveredPages.size,
    totalPages,
    percentage: Math.round((coveredPages.size / totalPages) * 100),
  };
};

export const useMetadocComputedValues = ({
  metadoc,
  metadocId,
  opsGroups,
  activeGroups,
  groupingSource,
  backgroundJobsData,
  isAIProcessing,
  isRunningAIModel,
}: UseMetadocComputedValuesArgs): UseMetadocComputedValuesReturn => {
  // calculate OPS page classification stats (for AI button gating)
  const opsPageStats = useMemo(
    () => calculatePageStats(metadoc?.page_count ?? null, opsGroups),
    [metadoc?.page_count, opsGroups],
  );

  // calculate active page classification stats (for display)
  const activePageStats = useMemo(
    () =>
      calculatePageStats(
        groupingSource === "AI"
          ? (metadoc?.analyst_page_count ?? metadoc?.page_count ?? null)
          : (metadoc?.page_count ?? null),
        activeGroups,
      ),
    [
      groupingSource,
      metadoc?.analyst_page_count,
      metadoc?.page_count,
      activeGroups,
    ],
  );

  // check if there's an active AI job for the current metadoc
  const hasActiveAIJob = useMemo(() => {
    if (!backgroundJobsData?.results || !metadocId) return false;
    const currentMetadocIdNum = parseInt(metadocId, 10);
    return backgroundJobsData.results.some(
      (job) =>
        job.metadoc_id === currentMetadocIdNum &&
        job.job_type === "AI Sort & Summary" &&
        (job.status === "Pending" || job.status === "In Progress"),
    );
  }, [backgroundJobsData, metadocId]);

  // AI button should be gated by OPS grouping completeness
  const isRunAiModelReady = useMemo(() => {
    return opsPageStats.percentage === 100;
  }, [opsPageStats.percentage]);

  // determine if button should be disabled
  const isRunAiModelDisabled =
    isRunningAIModel ||
    isAIProcessing ||
    hasActiveAIJob ||
    !metadoc ||
    metadoc.status !== "completed" ||
    !isRunAiModelReady;

  // get tooltip message for disabled button
  const runAiModelDisabledReason = useMemo(() => {
    if (!metadoc) return "Loading...";
    if (metadoc.status !== "completed") return "Metadoc must be completed";
    if (!isRunAiModelReady) {
      return "All pages must be grouped before running AI model";
    }
    return undefined;
  }, [metadoc, isRunAiModelReady]);

  // get current AI job status for display for the current metadoc
  const currentAIJob = useMemo(() => {
    if (!backgroundJobsData?.results || !metadocId) return null;
    const currentMetadocIdNum = parseInt(metadocId, 10);
    const aiJobs = backgroundJobsData.results.filter(
      (job) =>
        job.metadoc_id === currentMetadocIdNum &&
        job.job_type === "AI Sort & Summary",
    );
    return aiJobs.length > 0 ? aiJobs[0] : null;
  }, [backgroundJobsData, metadocId]);

  // determine the processing job status to display
  // if we're processing but don't have a job yet, show "Pending"
  const processingJobStatus = useMemo(() => {
    if (isAIProcessing && !currentAIJob) {
      // if we're processing but job doesn't exist yet, show pending
      return "Pending";
    }
    return currentAIJob?.status;
  }, [isAIProcessing, currentAIJob]);

  // find the most recent completed AI job for last run status
  const lastCompletedAIJob = useMemo(() => {
    if (!backgroundJobsData?.results) return null;
    const completedAIJobs = backgroundJobsData.results
      .filter(
        (job) =>
          job.job_type === "AI Sort & Summary" && job.status === "Success",
      )
      .sort((a, b) => {
        // sort by created date descending (most recent first)
        const dateA = new Date(a.created).getTime();
        const dateB = new Date(b.created).getTime();
        return dateB - dateA;
      });
    return completedAIJobs.length > 0 ? completedAIJobs[0] : null;
  }, [backgroundJobsData]);

  return {
    opsPageStats,
    activePageStats,
    isRunAiModelReady,
    isRunAiModelDisabled,
    runAiModelDisabledReason,
    hasActiveAIJob,
    currentAIJob,
    lastCompletedAIJob,
    processingJobStatus,
  };
};
